import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import FilterIcon from "../../assets/commonIcon/FilterIcon";
import { InputNumber } from "../../components/InputText/InputNumber";
import { CustomButton } from "../../components/ui/button/CustomButton/CustomButton";

interface RecipeSimulatorDetailsCardProps {
  currentImpactChange: number;
  setVisibleFilterColumnModal: (status: boolean) => void;
  productSize: number;
  setProductSize: (size: number) => void;
  readOnly?: boolean;
}

const RecipeSimulatorDetailsCard = ({
  currentImpactChange,
  setVisibleFilterColumnModal,
  productSize,
  setProductSize,
  readOnly = false
}: RecipeSimulatorDetailsCardProps) => {
  const unit = "kgCO2eq";

  const values = useMemo(() => {
    return {
      size: productSize || 0,
      currentImpactChange
    };
  }, [currentImpactChange, productSize]);

  const {
    formState: { errors },
    control,
    watch
  } = useForm({
    mode: "all",
    defaultValues: {
      size: productSize,
      currentImpactChange: currentImpactChange ?? 0
    },
    values: values
  });
  const productSizeChange = watch("size");

  useEffect(() => {
    const numericValue = Number(productSizeChange) || 0;
    setProductSize(numericValue);
  }, [productSizeChange]);

  return (
    <div className="tw-w-full">
      <div className="tw-overflow-visible tw-rounded-[5px] tw-border tw-border-primary tw-bg-white tw-p-5">
        <div className="tw-mb-5 tw-flex tw-items-center">
          <p className="tw-m-0 tw-text-lg tw-font-bold tw-text-primary">Impact per kg</p>
        </div>

        <div className="tw-w-full">
          <p className="tw-m-0 tw-text-foreground">Baseline Impact​</p>
          <p className="tw-m-0 tw-text-foreground">
            <span className="tw-text-xl tw-font-bold tw-text-[#8B9097]">
              {currentImpactChange?.toFixed(2)}
            </span>{" "}
            kgCO2eq/kg
          </p>
        </div>

        <div className="tw-relative tw-mx-2.5 tw-mb-2.5 tw-mt-6 tw-overflow-visible tw-rounded-md tw-border tw-border-primary tw-p-4 tw-pt-5 tw-text-primary">
          <div className="tw-absolute tw-left-0 tw-right-0 tw-top-[-12px] tw-flex tw-justify-center">
            <p className="tw-m-0 tw-bg-white tw-px-2.5 tw-py-1.5 tw-text-foreground">
              {unit} per production size
            </p>
          </div>

          <InputNumber
            control={control}
            errors={errors.size}
            labelName={null}
            placeholder="Production Size"
            rightContentEnable
            rightContent="kg"
            name="size"
            disabled={readOnly}
          />

          <div className="tw-pt-0 tw-text-center">
            <p className="tw-m-0 tw-text-[15px] tw-italic tw-text-foreground">
              Baseline Impact Total​
            </p>
            <p className="tw-m-0 tw-text-lg tw-italic tw-text-[#8B9097]">
              <span className="tw-font-bold">
                {(currentImpactChange * productSize).toFixed(2)}
              </span>{" "}
              kgCO2eq
            </p>
          </div>
        </div>
      </div>

      <div className="tw-my-4 tw-flex tw-justify-start">
        <CustomButton
          wrapInColumn={false}
          className="!tw-h-11 !tw-w-auto tw-px-5 tw-py-2.5 tw-text-base"
          iconWithText
          iconChild={<FilterIcon />}
          buttonOutLine
          theme="green"
          title="Filters"
          iconChildTextCustom="tw-ml-2 tw-text-base tw-font-medium tw-text-primary"
          onClick={() => setVisibleFilterColumnModal(true)}
        />
      </div>
    </div>
  );
};

export default RecipeSimulatorDetailsCard;
